import type { TonyteActivity } from '../types';
import { berlinDateTimeToMillis, millisToBerlinParts } from './promotionAdmin';

// =============================================================================
// Partner Calendar (in-app) — the same model as the partner portal's
// Tonyte_website/src/partnerCalendarModel.js. The calendar manages the
// partner's ordinary event docs; nothing here is a separate event type:
//   draft     private state every public path already hides
//   scheduled draft + scheduledPublishAt (published server-side at that time)
//   recurring eventSeries/{id} + one event doc per date, id `${seriesId}_${YYYYMMDD}`
// Dates/times are Europe/Berlin wall clock. Keep in sync with the portal.
// =============================================================================

export type CalendarStatus = 'draft' | 'scheduled' | 'published' | 'cancelled' | 'past';
export const CALENDAR_STATUSES: CalendarStatus[] = ['draft', 'scheduled', 'published', 'cancelled', 'past'];

export const DRAFT_FIELDS = { approved: false, isPublic: false, visibility: 'private', status: 'pending', publishStatus: 'draft' } as const;
export const PUBLISHED_FIELDS = { approved: true, isPublic: true, visibility: 'public', status: 'approved', publishStatus: 'published' } as const;

export const MAX_SERIES_OCCURRENCES = 52;
const DEFAULT_SERIES_WEEKS = 12;
const DAY_MS = 86400000;

type Raw = TonyteActivity & Record<string, any>;

const isDateKey = (v: unknown): v is string => /^\d{4}-\d{2}-\d{2}$/.test(String(v || ''));

export function addDays(key: string, days: number): string {
  const [y, m, d] = key.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d + days));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
}

export function berlinTodayKey(now = Date.now()): string {
  const p = millisToBerlinParts(now);
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}

const weekdayOf = (key: string) => new Date(`${key}T12:00:00Z`).getUTCDay();

export function isDraftDoc(raw: Raw): boolean {
  return raw.publishStatus === 'draft' || raw.visibility === 'private' || raw.isPublic === false || raw.approved === false;
}

// Start/end instants (Berlin wall clock). An end at/before the start on the
// same day runs past midnight (re-read on the next day — DST-safe); no end
// time = a 3-hour window.
export function eventWindow(raw: Raw): { start: number | null; end: number | null } {
  if (!isDateKey(raw.startDate)) return { start: null, end: null };
  const start = berlinDateTimeToMillis(raw.startDate, raw.startTime || '00:00');
  if (start === null) return { start: null, end: null };
  let end: number | null = null;
  if (raw.endTime) {
    end = berlinDateTimeToMillis(isDateKey(raw.endDate) ? raw.endDate : raw.startDate, raw.endTime);
    if (end !== null && end <= start) {
      end = isDateKey(raw.endDate) && raw.endDate !== raw.startDate ? start + 3 * 3600000 : berlinDateTimeToMillis(addDays(raw.startDate, 1), raw.endTime);
    }
  } else if (isDateKey(raw.endDate) && raw.endDate !== raw.startDate) {
    end = berlinDateTimeToMillis(raw.endDate, '23:59');
  }
  return { start, end: end ?? start + 3 * 3600000 };
}

export function scheduledPublishMillis(raw: Raw): number | null {
  const v = raw.scheduledPublishAt as any;
  if (!v) return null;
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (typeof v.seconds === 'number') return v.seconds * 1000;
  const ms = Date.parse(v);
  return Number.isNaN(ms) ? null : ms;
}

export function calendarStatus(raw: Raw, now = Date.now()): CalendarStatus {
  if (raw.status === 'cancelled') return 'cancelled';
  const { end } = eventWindow(raw);
  if (end !== null && end < now) return 'past';
  if (isDraftDoc(raw)) return raw.scheduledPublishAt ? 'scheduled' : 'draft';
  return 'published';
}

export type EventSourceKey = 'tonyte' | 'luma' | 'luma-detached' | 'import';

export function eventSource(raw: Raw): EventSourceKey {
  if (raw.externalSource?.provider === 'luma') return raw.externalSource.detached ? 'luma-detached' : 'luma';
  if (raw.importSource) return 'import';
  return 'tonyte';
}

export function missingDetails(raw: Raw): { required: string[]; recommended: string[] } {
  const text = (v: unknown) => String(v || '').trim();
  const required: string[] = [];
  if (!text(raw.title)) required.push('title');
  if (!text(raw.description)) required.push('description');
  if (!text(raw.address || raw.location)) required.push('location');
  if (!text(raw.startDate)) required.push('start date');
  if (!text(raw.startTime)) required.push('start time');
  if (!Array.isArray(raw.tags) || !raw.tags.length) required.push('tags');
  if (raw.isFree !== true && raw.requiresTicket !== true) required.push('free or paid');
  const recommended: string[] = [];
  if (!(raw.coverPhoto || raw.coverImageUrl)) recommended.push('cover image');
  return { required, recommended };
}

export function eventDayKeys(raw: Raw, max = 31): string[] {
  if (!isDateKey(raw.startDate)) return [];
  const last = isDateKey(raw.endDate) && raw.endDate > raw.startDate ? raw.endDate : raw.startDate;
  const keys: string[] = [];
  for (let key = raw.startDate; key <= last && keys.length < max; key = addDays(key, 1)) keys.push(key);
  return keys;
}

export function groupByDay<T extends { raw: Raw }>(items: T[]): Map<string, T[]> {
  const byDay = new Map<string, T[]>();
  items.forEach(item => eventDayKeys(item.raw).forEach(key => {
    if (!byDay.has(key)) byDay.set(key, []);
    byDay.get(key)!.push(item);
  }));
  byDay.forEach(list => list.sort((a, b) => String(a.raw.startTime || '').localeCompare(String(b.raw.startTime || ''))));
  return byDay;
}

// Monday-first 6x7 grid of date keys (month 1-12).
export function monthGrid(year: number, month: number): string[][] {
  const first = `${year}-${String(month).padStart(2, '0')}-01`;
  const offset = (new Date(Date.UTC(year, month - 1, 1)).getUTCDay() + 6) % 7;
  let key = addDays(first, -offset);
  const weeks: string[][] = [];
  for (let w = 0; w < 6; w += 1) {
    const days: string[] = [];
    for (let d = 0; d < 7; d += 1) {
      days.push(key);
      key = addDays(key, 1);
    }
    weeks.push(days);
  }
  return weeks;
}

export type SeriesRule = { pattern: string; weekdays?: number[]; until?: string; count?: string | number; extraDates?: string[]; skipDates?: string[] };

export function expandRecurrence(rule: SeriesRule | null | undefined, startDate: string, max = MAX_SERIES_OCCURRENCES): string[] {
  if (!isDateKey(startDate)) return [];
  const skip = new Set((rule?.skipDates || []).filter(isDateKey));
  const dates = new Set([startDate]);
  if (rule?.pattern === 'weekly') {
    const weekdays = new Set((rule.weekdays || []).map(Number).filter(n => n >= 0 && n <= 6));
    if (!weekdays.size) weekdays.add(weekdayOf(startDate));
    const count = Number(rule.count) > 0 ? Math.min(Number(rule.count), max) : null;
    const until = isDateKey(rule.until) ? rule.until : (count ? null : addDays(startDate, DEFAULT_SERIES_WEEKS * 7 - 1));
    for (let key = addDays(startDate, 1); dates.size < (count || max); key = addDays(key, 1)) {
      if (until && key > until) break;
      if (key > addDays(startDate, 366 * 2)) break;
      if (weekdays.has(weekdayOf(key))) dates.add(key);
    }
  } else if (rule?.pattern === 'custom') {
    (rule.extraDates || []).filter(isDateKey).forEach(key => { if (key >= startDate) dates.add(key); });
  }
  return [...dates].filter(key => !skip.has(key)).sort().slice(0, max);
}

export const occurrenceId = (seriesId: string, dateKey: string) => `${seriesId}_${dateKey.replace(/-/g, '')}`;

export function daysBetween(fromKey: string, toKey: string): number {
  const [a, b] = [fromKey, toKey].map(k => { const [y, m, d] = k.split('-').map(Number); return Date.UTC(y, m - 1, d); });
  return Math.round((b - a) / DAY_MS);
}

export function shiftToDate(fields: { startDate?: string; endDate?: string }, dateKey: string) {
  const span = isDateKey(fields.endDate) && isDateKey(fields.startDate) ? Math.max(0, daysBetween(fields.startDate, fields.endDate)) : 0;
  return { startDate: dateKey, endDate: addDays(dateKey, span), occurrenceDate: dateKey };
}

const NON_SERIES_FIELDS = new Set([
  'startDate', 'endDate', 'occurrenceDate', 'time', 'startsAt', 'endsAt', 'seriesId', 'seriesException',
  'approved', 'isPublic', 'visibility', 'status', 'publishStatus', 'scheduledPublishAt', 'scheduleError', 'incomplete', 'missingFields', 'needsRevalidation',
  'participants', 'attendees', 'interestedCount', 'createdAt', 'ownerUid', 'createdBy', 'hostId', 'organizerId',
  'creatorId', 'uploadedBy', 'userId', 'partnerId', 'partnerProfileId', 'externalSource', 'cancelledAt', 'cancelledBy',
  'isWeeklyFeatured', 'featuredStartAt', 'featuredEndAt', 'featuredPriority', 'featuredBy', 'topPick',
  'recurrencePattern', 'recurrenceWeekdays', 'recurrenceEndDate', 'recurrenceOccurrenceCount', 'recurrenceExtraDates', 'recurrenceSkipDates', 'saveAsTemplate',
]);

export function seriesEditableFields(payload: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(payload).filter(([key, value]) => !NON_SERIES_FIELDS.has(key) && value !== undefined));
}

// Lifecycle fields an edit must keep: editing never silently publishes a
// draft or revives a cancelled date (publishing is an explicit calendar
// action). Luma drafts keep their existing "edit = publish" review flow.
export function lifecycleToKeep(raw: Raw | undefined | null): Record<string, unknown> | null {
  if (!raw) return null;
  if (raw.status === 'cancelled') {
    return { status: 'cancelled', approved: false, isPublic: false, visibility: raw.visibility || 'public', publishStatus: raw.publishStatus || 'published' };
  }
  if (raw.externalSource?.provider === 'luma' && raw.publishStatus === 'draft') return null;
  if (isDraftDoc(raw)) return { ...DRAFT_FIELDS };
  return null;
}

export const isLegacyRecurring = (raw: Raw) => !raw.seriesId && (raw.recurrencePattern === 'weekly' || raw.recurrencePattern === 'custom');

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function dayLabel(key: string): string {
  if (!isDateKey(key)) return '';
  const [y, m, d] = key.split('-').map(Number);
  return `${WEEKDAYS[weekdayOf(key)]} ${d} ${MONTHS[m - 1]} ${y}`;
}

export function formatBerlinShort(ms: number | null): string {
  if (ms === null) return '';
  const p = millisToBerlinParts(ms);
  return `${p.day} ${MONTHS[p.month - 1]}, ${String(p.hour).padStart(2, '0')}:${String(p.minute).padStart(2, '0')}`;
}
